import { Card, CardActions, CardContent, Grid, Typography } from '@mui/material';
import { useFormik } from 'formik';
import { PmiButton } from 'shell/components/shared/button';
import { useCallback } from 'react';
import * as yup from 'yup';
import RegionList from '../region-list';
import PmiTextField from 'shell/components/shared/text-field';

export interface ProvinceForm {
  code?: string;
  name?: string;
  region?: string;
}

const validationSchema = yup.object().shape({
  region: yup.string().required('Il campo regione è obbligatorio'),
  code: yup.string().required('Il campo codice provincia è obbligatorio'),
  name: yup.string().required('Il campo nome provincia è obbligatorio'),
});

interface FormProvinceProps {
  onOk: (form: ProvinceForm) => void;
  onCancel: () => void;
  title: string;
  disabled?: boolean;
  initialValues?: ProvinceForm;
  isEdit?: boolean;
}

const FormProvince = ({
  onOk,
  onCancel,
  title,
  disabled = false,
  initialValues,
  isEdit = false,
}: FormProvinceProps): React.ReactElement => {
  const onSubmit = useCallback(
    (formValues) => {
      onOk(formValues);
    },
    [onOk]
  );

  const formProvince = useFormik<ProvinceForm>({
    initialValues: initialValues || {
      code: '',
      name: '',
      region: '',
    },
    validationSchema: validationSchema,
    onSubmit,
  });

  return (
    <Card raised className="pmi-my-3">
      <form onSubmit={formProvince.handleSubmit}>
        <CardContent style={{ padding: '24px' }}>
          <Typography gutterBottom variant="h5" component="h2" color="secondary">
            {title}
          </Typography>
          <div style={{ padding: '24px 0 0 0' }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={3}>
                <PmiTextField
                  aria-label="Sigla provincia"
                  label="Sigla provincia *"
                  id="code"
                  name="code"
                  type="text"
                  variant="outlined"
                  fullWidth
                  value={formProvince.values.code || ''}
                  inputProps={{
                    maxLength: '2',
                  }}
                  disabled={disabled || isEdit}
                  onChange={formProvince.handleChange}
                  error={formProvince.touched.code && Boolean(formProvince.errors.code)}
                  helperText={formProvince.errors.code}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <PmiTextField
                  aria-label="Nome provincia"
                  label="Nome provincia *"
                  id="name"
                  name="name"
                  fullWidth
                  type="text"
                  variant="outlined"
                  inputProps={{
                    maxLength: '24',
                  }}
                  value={formProvince.values.name || ''}
                  disabled={disabled}
                  onChange={formProvince.handleChange}
                  error={formProvince.touched.name && Boolean(formProvince.errors.name)}
                  helperText={formProvince.errors.name}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <RegionList
                  required
                  value={formProvince.values.region || ''}
                  onChange={formProvince.handleChange}
                  error={formProvince.touched.region && Boolean(formProvince.errors.region)}
                  disabled={disabled}
                />
              </Grid>
            </Grid>
          </div>
        </CardContent>
        <CardActions style={{ padding: '24px 24px 16px 24px' }}>
          <PmiButton
            variant="contained"
            color="primary"
            type="submit"
            disabled={disabled || !formProvince.isValid}
          >
            Conferma
          </PmiButton>
          <PmiButton
            variant="outlined"
            color="cdmBrand"
            onClick={() => {
              onCancel();
            }}
          >
            Indietro
          </PmiButton>
        </CardActions>
      </form>
    </Card>
  );
};

FormProvince.displayName = 'Form Province component';

export default FormProvince;
